import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const RANK = { TOP: 0, REGULAR: 1, OCCASIONAL: 2, NEW: 3 };
const byLoyalty = (a, b) => (RANK[a.loyalty?.tier] ?? 3) - (RANK[b.loyalty?.tier] ?? 3);

/* Repeat-business highlight: regular / key accounts are shown first with a gold card so they get the best rates. */
function LoyaltyBadge({ loyalty }) {
  if (!loyalty || (loyalty.tier !== "REGULAR" && loyalty.tier !== "TOP")) return null;
  return (
    <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-100 border border-amber-300 px-3 py-2 text-xs text-amber-900" data-testid="loyalty-badge">
      <Star size={14} className="mt-0.5 fill-amber-500 text-amber-500 shrink-0" />
      <div><b>{loyalty.tier === "TOP" ? "Key account" : "Repeat customer"}</b> — {loyalty.note}
        {loyalty.last_booking && <span className="text-amber-800"> Last booked {new Date(loyalty.last_booking).toLocaleDateString("en-IN")}.</span>}</div>
    </div>
  );
}
const cardCls = (l) => `bg-white border rounded-xl p-4 ${l && (l.tier === "REGULAR" || l.tier === "TOP") ? "border-amber-400 ring-1 ring-amber-200" : "border-slate-200"}`;

export default function CustomerApprovals() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const base = isAdmin ? "/admin" : "/staff";
  const [tab, setTab] = useState("new");
  const [status, setStatus] = useState("PENDING");
  const [newCust, setNewCust] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/v2/customer-changes/new-customers/list", { params: { status: status === "PENDING" ? "PENDING_APPROVAL" : "ALL" } }),
        api.get("/v2/customer-changes", { params: { status } }),
      ]);
      setNewCust([...(a.data || [])].sort(byLoyalty)); setReqs([...(b.data || [])].sort(byLoyalty));
    } catch { toast.error("Could not load approvals"); }
    setLoading(false);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (url, kind) => {
    let comment = "";
    if (kind === "reject") { comment = window.prompt("Reason for rejecting (required):") || ""; if (!comment.trim()) return; }
    setBusy(url);
    try { await api.post(url, { comment }); toast.success(kind === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Failed"); }
    setBusy(null);
  };

  const tabBtn = (k, label, n) => (
    <button onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500"}`}>
      {label}{status === "PENDING" && n > 0 ? <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-2 py-0.5">{n}</span> : null}
    </button>
  );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Customer Approvals</h1>
            <p className="text-sm text-slate-600">Business (B2B) customers and staff-requested changes wait here until an admin decides. Retail customers need no approval. Repeat customers are shown first — honour regular business with the best rates.</p>
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-sm">
            <option value="PENDING">Pending</option><option value="ALL">All (history)</option>
          </select>
        </div>
        <div className="border-b flex">{tabBtn("new", "New customers", newCust.length)}{tabBtn("changes", "Change requests", reqs.length)}</div>
        {loading ? <div className="py-12 text-center"><Loader2 className="animate-spin inline" /></div> : tab === "new" ? (
          newCust.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">Nothing here.</p> :
            <div className="space-y-3">{newCust.map((c) => (
              <div key={c.customer_id} className={`${cardCls(c.loyalty)} flex flex-wrap justify-between gap-3`}>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">{c.company_name || c.contact_person}{c.customer_code && <span className="ml-2 font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{c.customer_code}</span>} <span className="text-xs text-slate-500">({c.customer_type})</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${c.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : c.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{c.status}</span></div>
                  <div className="text-slate-600">{c.contact_person} · {c.mobile} · {c.email}</div>
                  <div className="text-slate-600">GSTIN {fmt(c.gst_number)} · PAN {fmt(c.pan_number)}</div>
                  <div className="text-slate-600">Rate: {fmt(c.rate_agreement.type)}{c.rate_agreement.markup_percent ? ` · markup ${c.rate_agreement.markup_percent}%` : ""}{c.rate_agreement.has_file ? ` · file ${c.rate_agreement.rate_card_filename}` : ""} · credit limit ₹{c.credit_limit || 0}</div>
                  <LoyaltyBadge loyalty={c.loyalty} />
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`${base}/customers/${c.customer_id}`} className="text-blue-700 text-sm inline-flex items-center gap-1"><ExternalLink size={14} />Open</Link>
                  {isAdmin && c.status === "PENDING_APPROVAL" && (<>
                    <button disabled={busy} onClick={() => decide(`/v2/customer-changes/new-customers/${c.customer_id}/approve`, "approve")} className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1"><CheckCircle2 size={14} />Approve</button>
                    <button disabled={busy} onClick={() => decide(`/v2/customer-changes/new-customers/${c.customer_id}/reject`, "reject")} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm inline-flex items-center gap-1"><XCircle size={14} />Reject</button>
                  </>)}
                </div>
              </div>))}</div>
        ) : (
          reqs.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">Nothing here.</p> :
            <div className="space-y-3">{reqs.map((r) => (
              <div key={r.id} className={cardCls(r.loyalty)}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div className="text-sm"><b>{r.customer_name}</b> <span className="text-slate-500">· requested by {r.requested_by} · {new Date(r.created_at).toLocaleString("en-IN")}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${r.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : r.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{r.status}</span></div>
                  <div className="flex items-center gap-2">
                    <Link to={`${base}/customers/${r.customer_id}`} className="text-blue-700 text-sm inline-flex items-center gap-1"><ExternalLink size={14} />Customer</Link>
                    {isAdmin && r.status === "PENDING" && (<>
                      <button disabled={busy} onClick={() => decide(`/v2/customer-changes/${r.id}/approve`, "approve")} className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm">Approve &amp; apply</button>
                      <button disabled={busy} onClick={() => decide(`/v2/customer-changes/${r.id}/reject`, "reject")} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm">Reject</button>
                    </>)}
                  </div>
                </div>
                <LoyaltyBadge loyalty={r.loyalty} />
                {r.reason && <div className="text-xs text-slate-600 mt-1">Reason: {r.reason}</div>}
                {r.comment && <div className="text-xs text-slate-600">Admin note: {r.comment}</div>}
                <table className="mt-2 text-sm w-full"><tbody>{r.changes.map((c) => (
                  <tr key={c.field} className="border-t border-slate-100">
                    <td className="py-1 pr-3 text-slate-500 w-48">{c.label}{c.sensitive && <span className="ml-1 text-xs text-amber-700">(sensitive)</span>}</td>
                    <td className="py-1 pr-3 text-red-700 line-through">{fmt(c.old)}</td><td className="py-1 text-emerald-700 font-medium">{fmt(c.new)}</td></tr>))}</tbody></table>
              </div>))}</div>
        )}
      </div>
    </DashboardLayout>
  );
}
